import { Box, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';
import type { VideoMetadata } from '../../../services/admin/adminBackendServiceVideoLibraryMixin';
import AddSlideCardContent from './AddSlideCardContent';
import VideoMetadataPromptDialog from './VideoMetadataPromptDialog';
import SlidePasteButton from './SlidePasteButton';
import { useAddSlideCardInteractions } from './useAddSlideCardInteractions';
import { useEditorSidebar } from './editorSidebarContext';
import { VIDEO_ACCEPT } from './useCardVideoUpload';
import { useLibraryVideoDrop } from './useLibraryVideoDrop';

const CARD_SX = {
  position: 'relative',
  flex: '1 1 260px',
  minWidth: 240,
  maxWidth: 360,
  minHeight: 260,
  border: '2px dashed',
  borderColor: 'divider',
  borderRadius: 2,
  bgcolor: '#fafafa',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  p: 2,
  textAlign: 'center',
  transition: 'border-color 0.12s ease, background-color 0.12s ease',
  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
} as const;

function addSlideCardSx(videoDropOver: boolean, draggingVideoId: string | null) {
  if (videoDropOver) return { ...CARD_SX, borderColor: 'primary.main', bgcolor: 'action.selected' };
  if (draggingVideoId) return { ...CARD_SX, borderColor: 'primary.light' };
  return CARD_SX;
}

function AddSlideCardTile({
  adding,
  error,
  accept,
  inputRef,
  interactions,
  videoDrop,
  draggingVideoId,
  promptOpen,
  onFile,
  onVideoUrl,
  onStashVideo,
}: {
  adding: boolean;
  error?: string | null;
  accept: string;
  inputRef: React.RefObject<HTMLInputElement>;
  interactions: ReturnType<typeof useAddSlideCardInteractions>;
  videoDrop: ReturnType<typeof useLibraryVideoDrop>;
  draggingVideoId: string | null;
  promptOpen: boolean;
  onFile: (file: File) => void;
  onVideoUrl?: (url: string) => void;
  onStashVideo: (file: File) => void;
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label="Add a slide"
      onClick={promptOpen ? undefined : interactions.open}
      onKeyDown={promptOpen ? undefined : interactions.onKeyDown}
      onDragOver={interactions.onDragOver}
      onDragLeave={videoDrop.onDragLeave}
      onDrop={(event) => interactions.onDrop(event, onStashVideo)}
      onPaste={interactions.onPaste}
      sx={addSlideCardSx(videoDrop.over, draggingVideoId)}
    >
      <Box sx={{ position: 'absolute', top: 6, right: 6 }}>
        <SlidePasteButton
          ariaLabel="Paste image to add a slide"
          onFile={onFile}
          onVideoUrl={onVideoUrl}
        />
      </Box>
      <AddSlideCardContent adding={adding} />
      <input
        type="file"
        accept={accept}
        ref={inputRef}
        style={{ display: 'none' }}
        onChange={(event) => interactions.onInputChange(event, onStashVideo)}
      />
      {error && (
        <Typography variant="caption" color="error.main">
          {error}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The dashed "New slide" tile — adds one carousel slide from a dropped, pasted, or picked
 * image, or a video slide from a pasted YouTube/Vimeo link, a library video dragged in
 * from the sidebar's Media tab, or a dropped or picked video file (uploaded to the library).
 */
export default function AddSlideCard({
  adding,
  onFile,
  onVideoUrl,
  onLibraryVideo,
  onVideoFile,
  error,
}: {
  adding: boolean;
  onFile: (file: File) => void;
  onVideoUrl?: (url: string) => void;
  onLibraryVideo?: (externalId: string) => void;
  /** A dropped or picked video file, with the title/description asked for it: added to the library and used for the new slide. */
  onVideoFile?: (file: File, metadata: VideoMetadata) => void;
  error?: string | null;
}) {
  const { draggingVideoId } = useEditorSidebar();
  const videoDrop = useLibraryVideoDrop(onLibraryVideo);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [stashedVideo, setStashedVideo] = useState<File | null>(null);
  const interactions = useAddSlideCardInteractions({
    adding,
    onFile,
    onVideoUrl,
    onVideoFile,
    videoDrop,
    inputRef,
  });

  return (
    <>
      <AddSlideCardTile
        adding={adding}
        error={error}
        accept={onVideoFile ? `image/*,${VIDEO_ACCEPT}` : 'image/*'}
        inputRef={inputRef}
        interactions={interactions}
        videoDrop={videoDrop}
        draggingVideoId={draggingVideoId}
        promptOpen={stashedVideo !== null}
        onFile={onFile}
        onVideoUrl={onVideoUrl}
        onStashVideo={setStashedVideo}
      />
      <VideoMetadataPromptDialog
        open={stashedVideo !== null}
        fileName={stashedVideo?.name}
        onCancel={() => setStashedVideo(null)}
        onConfirm={(title, description) => {
          const file = stashedVideo!;
          setStashedVideo(null);
          onVideoFile?.(file, { publicTitle: title, publicDescription: description });
        }}
      />
    </>
  );
}
